import { AppState } from "../system/AppState";
import { LocalDateTime } from "@js-joda/core";
import { useQuery, UseQueryResult } from "@tanstack/react-query";
import assertNever from "assert-never";
import React from "react";
import { Page } from "../activities/AppPage";
import { API } from "../API";
import { callNumber } from "../external_link";
import useCaregiver from "../hooks/useCaregiver";
import useMixin from "../hooks/useMixin";
import useTimeSlot from "../hooks/useTimeSlot";
import { isLoading } from "../Loading";
import {
  PatientTaskInstanceId,
  AddressComponents,
  GetPatientTimeSlotsResponse,
  AgencyId,
  PatientTaskInstanceDetails,
} from "../messages";
import { STRINGS } from "../strings";
import { AppCaregiver } from "../system/AppState/AppCaregiverState";
import { AppChatState } from "../system/AppState/ChatState";
import { globalize, translate } from "../translate";
import { getLocationDisplayLocation } from "../utils/addressComponentUtils";
import { useModalV2, ModalActions } from "./AppModals";
import { AvailableTaskScreen } from "./AvailableTask/AvailableTaskScreen";
import { AppLocationModal } from "./Generic/AppLocationModal";
import AppModal from "./Generic/AppModal/AppModal";
import { isDefined, IsDefined } from "./Generic/IsDefined";
import LoadingScaffold from "./LoadingScaffold";
import DirectionsBottomSheet from "./Patient/components/DirectionsBottomSheet";
import { useCallNumber } from "./Patient/useCallNumber";
import useAvailableTaskEngagement from "./useAvailableTaskEngagement";
import useDirections from "./useDirections";
import { useMedflytAgencyId } from "./withMedflytAgencyId";
import useUpdateScheduledTask from "./AvailableTask/hooks/useUpdateScheduledTask";
import { TaskInstanceChangeScreen } from "./AvailableTask/TaskInstanceChangeScreen";

interface Props {
  appState: AppState;
  api: API;
  patientTaskInstanceId: PatientTaskInstanceId;
  onClickChatWithAgency: (patientTaskInstanceId: PatientTaskInstanceId) => void;
  onRequestNavigate: (page: Page) => void;
  onClickGoBack: () => void;
}

type AvailableTaskState =
  | {
      type: "Loading";
    }
  | {
      type: "Ready";
      caregiver: AppCaregiver;
      taskDetails: PatientTaskInstanceDetails;
      countNewPatientChatMessages: number;
      isLocationModalOpen: boolean;
    };

const initialState: AvailableTaskState = { type: "Loading" };

const PatientTaskInstanceChangeActivity = (props: Props) => {
  const [state, setState] = React.useState<AvailableTaskState>(initialState);

  const mixin = useMixin(props.appState);
  const caregiver = useCaregiver(props.appState);
  const medflytAgencyId = useMedflytAgencyId();

  const handleCloseLocationModal = () => {
    setState((prev) => ({
      ...prev,
      isLocationModalOpen: false,
    }));
  };

  const handleOpenLocationModal = () => {
    setState((prev) => ({
      ...prev,
      isLocationModalOpen: true,
    }));
  };

  React.useEffect(() => {
    if (
      isLoading(caregiver) ||
      isLoading(mixin.appTasksState) ||
      isLoading(mixin.appAgenciesState) ||
      isLoading(mixin.appChatState) ||
      isLoading(mixin.appTaskBroadcastsState) ||
      isLoading(medflytAgencyId)
    ) {
      return;
    }

    const taskInstance = mixin.appTasksState.tasks.find(
      (x) => x.id === props.patientTaskInstanceId
    );

    if (!isDefined(taskInstance)) {
      console.log(
        `Task broadcast id ${props.patientTaskInstanceId} could not be found. redirecting back.`
      );
      return props.onClickGoBack();
    }

    const agency = mixin.appAgenciesState.agencies.get(medflytAgencyId);

    if (!isDefined(agency)) {
      console.log(`Agency id ${medflytAgencyId} could not be found. redirecting back.`);
      return props.onClickGoBack();
    }

    const countNewPatientChatMessages = AppChatState.patientNumNewChatMessages(
      mixin.appChatState,
      taskInstance.taskDetails.patientId
    );

    setState({
      type: "Ready",
      caregiver: caregiver,
      taskDetails: {
        type: "Available",
        taskInstanceId: taskInstance.id,
        patientId: taskInstance.taskDetails.patientId,
        patientInitials: taskInstance.taskDetails.patientInitials,
        agencyPhoneNumber: taskInstance.taskDetails.agencyPhone,
        title: taskInstance.taskDetails.title,
        documents: taskInstance.taskDetails.documents,
        agency: agency.agency,
        durationMinutes: taskInstance.taskDetails.durationMinutes,
        patientAddress: taskInstance.taskDetails.patientAddress,
        patientAddressComponents: taskInstance.taskDetails.patientAddressComponent,
        patientAge: taskInstance.taskDetails.patientAge,
        patientGender: taskInstance.taskDetails.patientGender,
        patientName: taskInstance.taskDetails.patientName,
        patientPhoneNumber: taskInstance.taskDetails.patientMobilePhoneNumber,
        dayTimeParams: taskInstance.dayTimeParams,
        dueDate: taskInstance.dueDate,
        startDate: taskInstance.startDate,
        linkedPatientName: null,
        linkedPatientTaskInstanceId: null,
      },
      countNewPatientChatMessages: countNewPatientChatMessages,
      isLocationModalOpen: false,
    });
  }, [
    caregiver,
    medflytAgencyId,
    mixin.appAgenciesState,
    mixin.appChatState,
    mixin.appTaskBroadcastsState,
    mixin.appTasksState,
    props,
  ]);

  if (isLoading(caregiver)) {
    return <LoadingScaffold onClickGoBack={props.onClickGoBack} />;
  }

  switch (state.type) {
    case "Loading":
      return <LoadingScaffold onClickGoBack={props.onClickGoBack} />;
    case "Ready":
      return (
        <AvailableTaskActivityLoaded
          {...props}
          state={state}
          caregiver={caregiver}
          onCloseLocationModal={handleCloseLocationModal}
          onOpenLocationModal={handleOpenLocationModal}
        />
      );
    default:
      assertNever(state);
  }
};

function AvailableTaskActivityLoaded(
  props: Props & {
    state: AvailableTaskState & { type: "Ready" };
    caregiver: AppCaregiver;
    onCloseLocationModal: () => void;
    onOpenLocationModal: () => void;
  }
) {
  const { state } = props;
  const { openLocation, closeLocation, directionsState } = useDirections();

  const medflytAgencyId = useMedflytAgencyId();

  const handleClickLocation = (location: AddressComponents) => {
    openLocation([location.location.lat, location.location.lng]);
  };

  const hanldeGoBackAndRefreshTaskBroadcasts = () => {
    props.appState.refreshTaskBroadcasts();
    props.appState.refreshPatientTasks();
    props.onClickGoBack();
  };

  const { closeEngagementModal, engagementModalState, promptRequestTaskBroadcast } =
    useAvailableTaskEngagement({
      api: props.api,
      taskBroadcastId: props.patientTaskInstanceId,
      goBackAndRefreshTaskBroadcasts: hanldeGoBackAndRefreshTaskBroadcasts,
      onAssignSuccess: () => {
        props.appState.refreshPatients();
      },
    });

  const handleClickApprove = () => {
    promptRequestTaskBroadcast();
  };

  const handleClickCall = (phoneNumber: string | null) => {
    if (phoneNumber !== null) {
      callNumber(phoneNumber);
    }
  };

  return (
    <>
      {props.caregiver.shouldShowMainV2Screen && !isLoading(medflytAgencyId) ? (
        <AvailableTaskActivityLoadedMedflyt
          {...props}
          agencyId={medflytAgencyId}
          state={state}
          onOpenLocationModal={props.onOpenLocationModal}
          onClickCall={handleClickCall}
        />
      ) : (
        <AvailableTaskScreen
          caregiver={state.caregiver}
          onClickLocation={props.onOpenLocationModal}
          onClickApprove={handleClickApprove}
          onClickCall={handleClickCall}
          onClickChat={() => props.onClickChatWithAgency(state.taskDetails.taskInstanceId)}
          onClickGoBack={props.onClickGoBack}
          taskBroadcastDetails={state.taskDetails}
          onRequestNavigate={props.onRequestNavigate}
        />
      )}
      <DirectionsBottomSheet {...directionsState} onRequestClose={closeLocation} />
      <AppModal {...engagementModalState} onRequestClose={closeEngagementModal} />
      <IsDefined value={state.taskDetails.patientAddressComponents}>
        {(addressComponents) => (
          <AppLocationModal
            isOpen={state.isLocationModalOpen}
            originLocation={{
              lat: state.caregiver.addressGeoLocation.lat,
              lng: state.caregiver.addressGeoLocation.lng,
            }}
            destination={addressComponents}
            destinationText={getLocationDisplayLocation(addressComponents)}
            onClickDirections={() => handleClickLocation(addressComponents)}
            onRequestClose={props.onCloseLocationModal}
          />
        )}
      </IsDefined>
    </>
  );
}

export type GetTimeSlotsQuery = UseQueryResult<GetPatientTimeSlotsResponse, unknown>;

function AvailableTaskActivityLoadedMedflyt(
  props: Props & {
    agencyId: AgencyId;
    state: AvailableTaskState & { type: "Ready" };
    onOpenLocationModal: () => void;
    onClickCall: (phonenumber: string | null) => void;
  }
) {
  const { state } = props;
  const timeSlot = useTimeSlot();
  const callNumber = useCallNumber({ api: props.api, caregiver: state.caregiver });
  const { createModal } = useModalV2();

  const handleCallPatient = (scheduledDateTime: LocalDateTime) => {
    if (props.state.taskDetails.patientPhoneNumber === null) return;

    const dateTimeStr = globalize().formatLocalDateTime(scheduledDateTime, { datetime: "short" });
    const patientName = props.state.taskDetails.patientName;

    callNumber({
      name: props.state.taskDetails.patientName,
      destination: props.state.taskDetails.patientPhoneNumber,
      agencyId: props.state.taskDetails.agency.id,
      onClickCall: () => {
        const modal = createModal({
          id: "call-number",
          type: "neutral",
          title: translate(STRINGS.CONFIRM_PATIENT_AVAILABILITY),
          message: translate(STRINGS.CONFIRM_PATIENT_AVAILABILITY_MESSAGE)
            .replace("{{patientName}}", patientName)
            .replace("{{dateTime}}", dateTimeStr),
          actions: [
            {
              children: translate(STRINGS.YES_SCHEDULE_VISIT),
              key: "yes",
              onClick: () => {
                schedule.lateSchedule.mutate(scheduledDateTime);
                modal.remove();
              },
            },
            ModalActions.Close({
              children: translate(STRINGS.DONT_SCHEDULE_VISIT),
              onClick: () => modal.remove(),
            }),
          ],
        });
      },
      onClickCancel: () => schedule.openModal(scheduledDateTime),
    });
  };

  const schedule = useUpdateScheduledTask({
    api: props.api,
    taskBroadcast: state.taskDetails,
    scheduledDateTime: timeSlot.selectedDateTime,
    duration: state.taskDetails.durationMinutes,
    onSuccessfulSchedule: () => {
      props.appState.refreshTaskBroadcasts();
      props.appState.refreshPatientTasks();
    },
    onClickCallPatient: handleCallPatient,
    onRequestGoBack: props.onClickGoBack,
  });

  const getTimeSlotsQuery = useQuery({
    queryKey: [
      "time-slots",
      props.agencyId,
      props.state.taskDetails.patientId,
      props.state.taskDetails.taskInstanceId,
    ],
    queryFn: () =>
      props.api.getPatientTimeSlotsForNurse(
        props.agencyId,
        props.state.taskDetails.patientId,
        props.state.taskDetails.taskInstanceId
      ),
  });

  const baseProps = {
    caregiver: state.caregiver,
    onClickLocation: props.onOpenLocationModal,
    onSchedule: schedule.openModal,
    selectedDate: timeSlot.selectedDate,
    selectedTime: timeSlot.selectedTime,
    selectedDateTime: timeSlot.selectedDateTime,
    shouldShowError: timeSlot.shouldShowError,
    shouldShowTimeSlotError: timeSlot.shouldShowError,
    timeSlotAppCardRef: timeSlot.timeSlotAppCardRef,
    selectDateLabelRef: timeSlot.selectDateLabelRef,
    selectTimeLabelRef: timeSlot.selectTimeLabelRef,
    onSelectDate: timeSlot.selectDate,
    onSelectTime: timeSlot.selectTime,
    onShowError: timeSlot.showError,
    onClickGoBack: props.onClickGoBack,
    taskBroadcastDetails: state.taskDetails,
    onRequestNavigate: props.onRequestNavigate,
  };

  switch (getTimeSlotsQuery.status) {
    case "error":
    case "loading":
      return <TaskInstanceChangeScreen {...baseProps} slotsStatus={getTimeSlotsQuery.status} />;

    case "success":
      return (
        <TaskInstanceChangeScreen
          {...baseProps}
          slotsStatus="loaded"
          slots={getTimeSlotsQuery.data.slots}
          message={getTimeSlotsQuery.data.note}
        />
      );
  }
}

export default PatientTaskInstanceChangeActivity;
